// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tippy, { TippyProps } from "@tippyjs/react";
import { hideAll, Instance as TippyInstance, sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { TippyPopupStyle, tippyPopupStyles } from "@flip/App/Components/Tippy/Style";
import { importCssFiles } from "@flip/App/Components/Tippy/tippy.helper";
import { useTippyProps } from "@flip/App/Components/Tippy/tippy.hooks";
import { xHeaderUpdate } from "@flip/App/Handlers/XNG/Header";
import { constants } from "@flip/XDL/constants";
import { addScrollbarGutter, removeScrollbarGutter, xClassNames } from "@flip/XDL/Utilities";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { injectCss } from "@insite/mobius/utilities";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

export const XngTippy = styled(Tippy)<TippyProps>`
    ${injectCss}
`;

export interface TippyPopupProps extends TippyProps {
    /**
     * Show or hide close button.
     */
    showCloseButton?: "both" | "mobile" | "desktop" | false;
    /**
     * Show tooltip as menu like MiniCart or as tooltip.
     */
    type?: "menu" | "tooltip";
    /**
     * Show tooltip as left-side or default on mobile.
     */
    mobileMode?: "left-side" | "default";
    /**
     * Event will be fired when popup open and close.
     */
    onTriggerPopupOpen?: (isOpen: boolean) => void;
    /**
     * To remove span tag with element wrapper.
     */
    removeWrapper?: boolean;
    extendedStyles?: TippyPopupStyle;
    hideOverlay?: boolean;
}

type Props = Omit<TippyPopupProps, "children"> & {
    children?: React.ReactElement<any> | ((expanded: boolean) => React.ReactElement<any>);
};

const TippyPopup: FC<Props> = props => {
    const {
        type,
        onTriggerPopupOpen: onTriggerPopupOpenProps,
        showCloseButton,
        extendedStyles,
        children,
        removeWrapper,
        content: tippyContent,
        hideOverlay,
        ...otherProps
    } = props;

    const dispatch = useDispatch();
    const [styles] = useState(() => mergeWithCss(tippyPopupStyles, extendedStyles));
    const [instance, setInstance] = useState<TippyInstance>();
    const [expanded, setExpanded] = useState(false);
    const options = useTippyProps(otherProps);

    useEffect(() => {
        importCssFiles();
    }, []);

    const closeButtonHandler = () => {
        instance?.hide?.();
    };

    const onCreateHandler = (instance: TippyInstance) => {
        if (otherProps.mobileMode === "left-side") {
            instance.popper.className = xClassNames.append("tippy-menu-left-side", instance.popper.className);
            const compactHeaderHeight = document.getElementById("compact-header");
            const headerPage = document.getElementById("header-page");
            const headerHeight = compactHeaderHeight?.className.includes("shown-compact-header")
                ? compactHeaderHeight.getBoundingClientRect().height
                : headerPage?.getBoundingClientRect().height;

            instance.popper.style.setProperty(
                "--tippy-top-offset",
                `${headerHeight || constants.header.mobile.size.height}px`,
            );
        }

        setInstance(instance);
        otherProps.onCreate?.(instance);
    };

    const onShowHandler = (instance: TippyInstance) => {
        /**
         * Close all Tippy instances
         */
        hideAll();

        if (!hideOverlay) {
            /**
             * Show overlay menu
             */
            dispatch(xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_popup` }));

            /**
             * Add overflow: hidden for body and add scrollbar-gutter for html.
             * we need it to prevent page scroll when tippy opened
             */

            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        }
        
        otherProps.onShow?.(instance);
        onTriggerPopupOpenProps?.(true);
    };

    const onShownHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.append("menu-opened", instance.popper.className);
        }

        otherProps.onShown?.(instance);
        setExpanded(true);
    };

    const onHideHandler = (instance: TippyInstance) => {
        if (type === "menu") {
            instance.popper.className = xClassNames.remove("menu-opened", instance.popper.className);
        }
        if (!hideOverlay) {
            /**
             * Hide overlay menu
             */
            dispatch(xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_popup` }));

            /**
             * Remove overflow: hidden from body and add scrollbar-gutter from html.
             * we need it to make page scrollable after tippy closed
             */
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }

        otherProps.onHide?.(instance);
        onTriggerPopupOpenProps?.(false);
        setExpanded(false);
    };
    const renderedChildren = typeof children === "function" ? (children as any)(expanded) : children;
    return (
        <>
            <XngTippy
                {...options}
                onCreate={onCreateHandler}
                onShow={onShowHandler}
                onShown={onShownHandler}
                onHide={onHideHandler}
                content={
                    <StyledWrapper className="content-wrapper">
                        {showCloseButton && (
                            <Icon {...styles.closeButton} className="tippy-close-icon" onClick={closeButtonHandler} />
                        )}
                        {tippyContent}
                    </StyledWrapper>
                }
                {...styles.main}
            >
                {removeWrapper ? renderedChildren : <span style={styles.elementWrapper}>{renderedChildren}</span>}
            </XngTippy>
        </>
    );
};

TippyPopup.defaultProps = {
    showCloseButton: "both",
    mobileMode: "default",
    type: "tooltip",
    hideOverlay: false,
};

export default TippyPopup;
